import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDateLocale, useTimeZone } from '../../../../hooks'
import Button from '../../../Button'
import { PageEmptyState } from '../../../PageEmptyState'
import {
  ENTITY_PROFILE_TAB_UPDATES_INITIAL_VISIBLE_COUNT,
  ENTITY_PROFILE_TAB_UPDATES_SKELETON_INDICES,
  QA_NAME_ENTITY_PROFILE_TAB_UPDATES_SHOW_ALL,
} from '../EntityProfileTabUpdates.constants'
import {
  formatEntityProfileTabUpdateDateHeading,
  getEntityProfileTabUpdatesShowAllButtonText,
  getEntityProfileTabUpdatesSortedNewestFirst,
  groupEntityProfileTabUpdatesByDate,
} from '../EntityProfileTabUpdates.utils'
import { EntityProfileTabUpdatesCardSkeleton } from '../EntityProfileTabUpdatesCardSkeleton'
import { EntityProfileTabUpdatesListGroup } from '../EntityProfileTabUpdatesListGroup'

import * as styles from './EntityProfileTabUpdatesList.module.css'

import UpdatesIcon from '../../../../icons/refresh.svg'

function EntityProfileTabUpdatesList({
  deletedOwnerNameKey,
  emptyStateTextKey,
  errorStateTextKey,
  getThresholdItemProps,
  hasError,
  isLoading,
  isLoadingMore,
  items,
  systemOwnerNameKey,
}) {
  const { t } = useTranslation()

  const locale = useDateLocale()
  const timeZone = useTimeZone()

  const [showAll, setShowAll] = useState(false)

  const isInfiniteScroll = Boolean(getThresholdItemProps)

  const sortedItems = getEntityProfileTabUpdatesSortedNewestFirst(items)

  const visibleItems =
    isInfiniteScroll || showAll
      ? sortedItems
      : sortedItems.slice(0, ENTITY_PROFILE_TAB_UPDATES_INITIAL_VISIBLE_COUNT)

  const groups = groupEntityProfileTabUpdatesByDate(visibleItems, timeZone)

  function handleShowAll() {
    setShowAll(true)
  }

  return hasError || (sortedItems.length === 0 && !isLoading) ? (
    <PageEmptyState
      EntityIcon={UpdatesIcon}
      text={t(hasError ? errorStateTextKey : emptyStateTextKey)}
    />
  ) : (
    <div className={styles.list}>
      {isLoading ? (
        ENTITY_PROFILE_TAB_UPDATES_SKELETON_INDICES.map(index => (
          <EntityProfileTabUpdatesCardSkeleton key={index} />
        ))
      ) : (
        <>
          {groups.map(group => (
            <EntityProfileTabUpdatesListGroup
              dateHeading={formatEntityProfileTabUpdateDateHeading({
                locale,
                t,
                timestamp: group.timestamp,
                timeZone,
              })}
              deletedOwnerNameKey={deletedOwnerNameKey}
              getThresholdItemProps={getThresholdItemProps}
              items={group.items}
              key={group.dateKey}
              startIndex={group.startIndex}
              systemOwnerNameKey={systemOwnerNameKey}
            />
          ))}
          {isInfiniteScroll && isLoadingMore ? (
            <EntityProfileTabUpdatesCardSkeleton />
          ) : null}
          {!isInfiniteScroll &&
          !showAll &&
          sortedItems.length >
            ENTITY_PROFILE_TAB_UPDATES_INITIAL_VISIBLE_COUNT ? (
            <div className={styles.footer}>
              <Button.Outlined
                isGrey
                className={styles.showAllButton}
                qaName={QA_NAME_ENTITY_PROFILE_TAB_UPDATES_SHOW_ALL}
                text={getEntityProfileTabUpdatesShowAllButtonText({
                  t,
                  totalCount: sortedItems.length,
                })}
                onClick={handleShowAll}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

export default EntityProfileTabUpdatesList
